import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Info,
  SystemUpdate
} from '@mui/icons-material';
import VersionChecker from '../../utils/versionChecker';

interface VersionDisplayProps {
  /** Custom styling */
  sx?: object;
}

const VersionDisplay: React.FC<VersionDisplayProps> = ({ sx = {} }) => {
  const [currentVersion, setCurrentVersion] = useState<string>('');
  const [isCheckingVersion, setIsCheckingVersion] = useState<boolean>(false);
  const [lastReloadTime, setLastReloadTime] = useState<number>(0);
  const [showFeedback, setShowFeedback] = useState<{
    open: boolean;
    type: 'success' | 'info' | 'warning';
    message: string;
  }>({ open: false, type: 'success', message: '' });

  const RELOAD_COOLDOWN = 60000; // 1 minute cooldown between reloads  // Load current version on component mount
  useEffect(() => {
    const loadCurrentVersion = () => {
      try {
        const versionChecker = VersionChecker.getInstance();
        // Don't call init() - it should already be initialized globally
        const versionInfo = versionChecker.getVersionInfo();
        if (versionInfo.currentVersion) {
          setCurrentVersion(versionInfo.currentVersion);
        } else {
          // If not initialized yet, wait a bit and try again
          setTimeout(() => {
            const retryVersionInfo = versionChecker.getVersionInfo();
            if (retryVersionInfo.currentVersion) {
              setCurrentVersion(retryVersionInfo.currentVersion);
            }
          }, 1000);
        }
      } catch (error) {
        console.error('Failed to load version info:', error);
      }
    };

    loadCurrentVersion();
    
    // No automatic event listeners - manual checks only
  }, []);// Handle version check
  const handleVersionCheck = async () => {
    // Check cooldown for manual checks too
    const now = Date.now();
    if (now - lastReloadTime < RELOAD_COOLDOWN) {
      setShowFeedback({
        open: true,
        type: 'info',
        message: 'Please wait before checking for updates again.'
      });
      return;
    }
    
    setIsCheckingVersion(true);
    
    try {
      const hasNewVersion = await VersionChecker.getInstance().checkNowAndReload();
      if (hasNewVersion) {
        setLastReloadTime(now);
        setShowFeedback({
          open: true,
          type: 'warning',
          message: 'New version available! Page will reload to apply updates.'
        });
        // checkNowAndReload() will handle the reload automatically after a delay
      } else {
        setShowFeedback({
          open: true,
          type: 'success',
          message: 'You are running the latest version.'
        });
      }
    } catch (err) {
      console.error('Version check failed:', err);
      setShowFeedback({
        open: true,
        type: 'info',
        message: 'Unable to check for updates. Please try again later.'
      });
    } finally {
      setIsCheckingVersion(false);
    }
  };

  const handleCloseFeedback = () => {
    setShowFeedback({ ...showFeedback, open: false });
  };

  if (!currentVersion) {
    return null;
  }

  return (
    <>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, ...sx }}>
        <Tooltip title={`Application Version: ${currentVersion}`} arrow>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Info fontSize="small" color="inherit" />
            <Typography variant="caption" color="inherit">
              v{currentVersion}
            </Typography>
          </Box>
        </Tooltip>
        
        <Tooltip title="Check for updates" arrow>
          <IconButton
            size="small"
            color="inherit"
            onClick={handleVersionCheck}
            disabled={isCheckingVersion}
            sx={{ ml: 0.5 }}
          >
            {isCheckingVersion ? (
              <CircularProgress size={16} color="inherit" />
            ) : (
              <SystemUpdate fontSize="small" />
            )}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Feedback Snackbar */}
      <Snackbar
        open={showFeedback.open}
        autoHideDuration={5000}
        onClose={handleCloseFeedback}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseFeedback}
          severity={showFeedback.type}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {showFeedback.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default VersionDisplay;
